import axios from "axios";
import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import {
  REVIEW_WORKFLOW_STEP_ORDER,
  type ReviewWorkflowStep,
} from "@/components/case-checking/CaseReviewWorkflow";
import { useCaseDetailData } from "@/components/case-checking/CaseDetailDataContext";
import type { CompletenessReviewState } from "@/components/case-checking/ReviewCompletenessVerdict";
import {
  createPsRuleHumanReviewRun,
  getActivePsRuleHumanReviewRun,
  listProblemStatementRuns,
  patchGuidedReviewSession,
  saveGuidedReviewCopy,
  type GuidedReviewSessionPatch,
  type PsRuleHumanReviewRun,
} from "@/services/api/case-checking";
import {
  mergeHumanRuleReviews,
  normalizePsRuleHumanReviewRun,
} from "@/components/case-checking/humanRuleReviewCascade";

function toWorkflowStep(step: string | undefined): ReviewWorkflowStep {
  if (step && REVIEW_WORKFLOW_STEP_ORDER.includes(step as ReviewWorkflowStep)) {
    return step as ReviewWorkflowStep;
  }
  return "narrative";
}

/** Saved session fields that imply the reviewer reached at least a given workflow step. */
export type GuidedReviewProgressHints = Pick<
  PsRuleHumanReviewRun,
  "current_step" | "completeness_review" | "reviews" | "remedial_edits"
>;

function progressFloorIndex(hints?: GuidedReviewProgressHints | null): number {
  if (!hints) return -1;
  let maxIdx = REVIEW_WORKFLOW_STEP_ORDER.indexOf(toWorkflowStep(hints.current_step));
  if (hints.completeness_review != null) {
    maxIdx = Math.max(maxIdx, REVIEW_WORKFLOW_STEP_ORDER.indexOf("checklist"));
  }
  if ((hints.reviews?.length ?? 0) > 0) {
    maxIdx = Math.max(maxIdx, REVIEW_WORKFLOW_STEP_ORDER.indexOf("rules"));
  }
  if ((hints.remedial_edits?.length ?? 0) > 0) {
    maxIdx = Math.max(maxIdx, REVIEW_WORKFLOW_STEP_ORDER.indexOf("actions"));
  }
  return maxIdx;
}

/** Fill gaps when a later step was completed before an earlier one existed. */
export function normalizeCompletedSteps(
  steps: ReviewWorkflowStep[],
  currentStep?: ReviewWorkflowStep,
  progressHints?: GuidedReviewProgressHints | null,
): ReviewWorkflowStep[] {
  if (currentStep === "summary") {
    return [...REVIEW_WORKFLOW_STEP_ORDER];
  }
  const allowed = new Set(REVIEW_WORKFLOW_STEP_ORDER);
  const set = new Set(steps.filter((s) => allowed.has(s)));
  let maxIdx = progressFloorIndex(progressHints);
  for (const s of set) {
    maxIdx = Math.max(maxIdx, REVIEW_WORKFLOW_STEP_ORDER.indexOf(s));
  }
  for (let i = 0; i <= maxIdx; i++) {
    set.add(REVIEW_WORKFLOW_STEP_ORDER[i]);
  }
  return REVIEW_WORKFLOW_STEP_ORDER.filter((s) => set.has(s));
}

function completedStepsEqual(a: ReviewWorkflowStep[], b: ReviewWorkflowStep[]): boolean {
  return a.length === b.length && a.every((step, index) => step === b[index]);
}

function workflowStepIndex(step: ReviewWorkflowStep): number {
  const idx = REVIEW_WORKFLOW_STEP_ORDER.indexOf(step);
  return idx < 0 ? 0 : idx;
}

/** Keep the furthest workflow step when PATCH responses omit or regress session progress. */
export function mergeReviewRunSessionProgress(
  existing: PsRuleHumanReviewRun | null,
  incoming: PsRuleHumanReviewRun,
): PsRuleHumanReviewRun {
  if (!existing || existing.id !== incoming.id) {
    const step = toWorkflowStep(incoming.current_step);
    return {
      ...normalizePsRuleHumanReviewRun(incoming),
      current_step: step,
      completed_steps: normalizeCompletedSteps(incoming.completed_steps ?? [], step, incoming),
    };
  }

  const existingStep = toWorkflowStep(existing.current_step);
  const incomingStep = toWorkflowStep(incoming.current_step);
  const current_step =
    workflowStepIndex(incomingStep) >= workflowStepIndex(existingStep) ? incomingStep : existingStep;
  const completed_steps = normalizeCompletedSteps(
    [...(existing.completed_steps ?? []), ...(incoming.completed_steps ?? [])],
    current_step,
    incoming,
  );
  const reviews = mergeHumanRuleReviews(existing.reviews ?? [], incoming.reviews ?? []);
  return { ...incoming, current_step, completed_steps, reviews };
}

function applyActiveReviewRun(
  caseId: string,
  active: PsRuleHumanReviewRun | null,
  cancelledRef: MutableRefObject<boolean>,
  setSession: (run: PsRuleHumanReviewRun | null) => void,
  sessionRef: React.MutableRefObject<PsRuleHumanReviewRun | null>,
) {
  if (!active) {
    setSession(null);
    sessionRef.current = null;
    return;
  }
  const normalizedRun = normalizePsRuleHumanReviewRun(active);
  const normalized = normalizeCompletedSteps(
    normalizedRun.completed_steps ?? [],
    toWorkflowStep(normalizedRun.current_step),
    normalizedRun,
  );
  const next = completedStepsEqual(normalized, normalizedRun.completed_steps ?? [])
    ? normalizedRun
    : { ...normalizedRun, completed_steps: normalized };
  setSession(next);
  sessionRef.current = next;
  if (!completedStepsEqual(normalized, normalizedRun.completed_steps ?? [])) {
    void patchGuidedReviewSession(caseId, normalizedRun.id, { completed_steps: normalized }).then(
      (updated) => {
        if (!cancelledRef.current) {
          const merged = mergeReviewRunSessionProgress(sessionRef.current, normalizePsRuleHumanReviewRun(updated));
          setSession(merged);
          sessionRef.current = merged;
        }
      },
    );
  }
}

/**
 * Load (and optionally create) the active guided review session for a case.
 *
 * @param caseId - Case being reviewed
 * @param enabled - Whether to load the session at all
 * @param createIfMissing - Create a session when none exists. Pass false for read-only views
 *   (e.g. summary) so viewing a case does not make the viewer the session owner.
 */
export function useGuidedReviewSession(caseId: string, enabled: boolean, createIfMissing = true) {
  const shared = useCaseDetailData();
  const useShared = Boolean(shared && shared.caseId === caseId);
  const setSharedPsRuleReviewRun = useShared ? shared!.setPsRuleReviewRun : null;

  const [session, setSession] = useState<PsRuleHumanReviewRun | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [localStep, setLocalStep] = useState<ReviewWorkflowStep>("narrative");
  const [localCompleted, setLocalCompleted] = useState<Set<ReviewWorkflowStep>>(() => new Set());
  const [localCompleteness, setLocalCompleteness] = useState<CompletenessReviewState | null>(null);
  const [localDisputes, setLocalDisputes] = useState<Record<string, string>>({});
  const [psRunId, setPsRunId] = useState<string | null>(null);
  const [restarting, setRestarting] = useState(false);
  const [acknowledgingCleared, setAcknowledgingCleared] = useState(false);
  const sessionRef = useRef<PsRuleHumanReviewRun | null>(null);
  const cancelledRef = useRef(false);
  const prevProblemStatementRunIdRef = useRef<string | null>(null);

  const clearLocalSessionState = useCallback(() => {
    setSession(null);
    sessionRef.current = null;
    setLocalStep("narrative");
    setLocalCompleted(new Set());
    setLocalCompleteness(null);
    setLocalDisputes({});
  }, []);

  const reloadSession = useCallback(async (): Promise<void> => {
    if (useShared && shared) {
      await shared.reloadActiveReview();
      return;
    }

    try {
      const runs = await listProblemStatementRuns(caseId, 1);
      const psRun = runs[0];
      if (!psRun) {
        setPsRunId(null);
        setSession(null);
        sessionRef.current = null;
        return;
      }
      setPsRunId(psRun.id);
      const active = await getActivePsRuleHumanReviewRun(caseId, psRun.id, true);
      applyActiveReviewRun(caseId, active, cancelledRef, setSession, sessionRef);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to reload review session");
    }
  }, [caseId, shared, useShared]);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const applyOptimistic = useCallback((next: PsRuleHumanReviewRun) => {
    setSession(next);
    sessionRef.current = next;
  }, []);

  const persistSession = useCallback(
    async (patch: GuidedReviewSessionPatch) => {
      const current = sessionRef.current;
      if (!current) return;
      try {
        const updated = await patchGuidedReviewSession(caseId, current.id, patch);
        const merged = mergeReviewRunSessionProgress(current, normalizePsRuleHumanReviewRun(updated));
        setSession(merged);
        sessionRef.current = merged;
        if (setSharedPsRuleReviewRun) setSharedPsRuleReviewRun(merged);
      } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 404) {
          await reloadSession();
          return;
        }
        setError(e instanceof Error ? e.message : "Failed to save review progress");
      }
    },
    [caseId, reloadSession, setSharedPsRuleReviewRun],
  );

  const acknowledgeClearedReview = useCallback(async (): Promise<boolean> => {
    const current = sessionRef.current;
    if (!current?.cleared_prior_review || current.cleared_prior_review.acknowledged_at) return true;
    setAcknowledgingCleared(true);
    setError(null);
    try {
      const updated = await patchGuidedReviewSession(caseId, current.id, {
        acknowledge_cleared_prior_review: true,
      });
      const merged = mergeReviewRunSessionProgress(current, normalizePsRuleHumanReviewRun(updated));
      setSession(merged);
      sessionRef.current = merged;
      if (setSharedPsRuleReviewRun) setSharedPsRuleReviewRun(merged);
      return true;
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        await reloadSession();
        return true;
      }
      setError(e instanceof Error ? e.message : "Failed to acknowledge notice");
      return false;
    } finally {
      setAcknowledgingCleared(false);
    }
  }, [caseId, reloadSession, setSharedPsRuleReviewRun]);

  useEffect(() => {
    const sharedRunId = useShared ? shared?.problemStatementRun?.id ?? null : null;
    const effectiveRunId = sharedRunId ?? psRunId;
    const prev = prevProblemStatementRunIdRef.current;
    if (prev !== null && effectiveRunId !== null && prev !== effectiveRunId) {
      clearLocalSessionState();
    }
    if (effectiveRunId !== null) {
      prevProblemStatementRunIdRef.current = effectiveRunId;
    }
  }, [clearLocalSessionState, psRunId, shared?.problemStatementRun?.id, useShared]);

  useEffect(() => {
    cancelledRef.current = false;
    if (!enabled) {
      setLoading(false);
      return () => {
        cancelledRef.current = true;
      };
    }

    if (useShared && shared) {
      if (shared.psRunLoading) {
        setLoading(true);
        return () => {
          cancelledRef.current = true;
        };
      }

      const run = shared.problemStatementRun;
      if (!run) {
        setPsRunId(null);
        setSession(null);
        sessionRef.current = null;
        setLoading(false);
        return () => {
          cancelledRef.current = true;
        };
      }

      setPsRunId(run.id);
      setError(shared.psRunError ?? shared.psRuleReviewError);

      if (shared.psRuleReviewRun) {
        applyActiveReviewRun(caseId, shared.psRuleReviewRun, cancelledRef, setSession, sessionRef);
        setLoading(false);
        return () => {
          cancelledRef.current = true;
        };
      }

      if (shared.psRuleReviewLoading) {
        setLoading(true);
        return () => {
          cancelledRef.current = true;
        };
      }

      if (shared.psRuleReviewFetchSettledForPsRunId === run.id) {
        applyActiveReviewRun(caseId, shared.psRuleReviewRun, cancelledRef, setSession, sessionRef);
        setLoading(false);
        return () => {
          cancelledRef.current = true;
        };
      }

      setLoading(true);
      void shared.ensurePsRuleReviewRun(createIfMissing).then((active) => {
        if (cancelledRef.current) return;
        applyActiveReviewRun(caseId, active, cancelledRef, setSession, sessionRef);
        setLoading(false);
      });

      return () => {
        cancelledRef.current = true;
      };
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    listProblemStatementRuns(caseId, 1)
      .then((runs) => {
        const psRun = runs[0];
        if (!psRun) {
          if (!cancelled) {
            setPsRunId(null);
            setSession(null);
            setLoading(false);
          }
          return null;
        }
        if (!cancelled) setPsRunId(psRun.id);
        return getActivePsRuleHumanReviewRun(caseId, psRun.id, createIfMissing);
      })
      .then((active) => {
        if (cancelled) return;
        applyActiveReviewRun(caseId, active, cancelledRef, setSession, sessionRef);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load review session");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      cancelledRef.current = true;
    };
  }, [
    caseId,
    enabled,
    useShared,
    shared?.caseId,
    shared?.psRunLoading,
    shared?.problemStatementRun,
    shared?.psRuleReviewRun,
    shared?.psRuleReviewLoading,
    shared?.psRuleReviewFetchSettledForPsRunId,
    shared?.psRunError,
    shared?.psRuleReviewError,
    shared?.ensurePsRuleReviewRun,
    shared,
    createIfMissing,
  ]);

  const persisted = Boolean(session);

  const reviewWorkflowStep = persisted ? toWorkflowStep(session?.current_step) : localStep;
  const completedWorkflowSteps = persisted
    ? new Set<ReviewWorkflowStep>(
        normalizeCompletedSteps(session?.completed_steps ?? [], reviewWorkflowStep, session),
      )
    : localCompleted;
  const completenessReview: CompletenessReviewState | null = persisted
    ? session?.completeness_review ?? null
    : localCompleteness;
  const checklistDisputes = persisted ? session?.checklist_disputes ?? {} : localDisputes;

  const markStepComplete = useCallback(
    (step: ReviewWorkflowStep) => {
      const current = sessionRef.current;
      if (!current) {
        setLocalCompleted((prev) => new Set(normalizeCompletedSteps([...prev, step])));
        return;
      }
      const completedSteps = normalizeCompletedSteps([...(current.completed_steps ?? []), step]);
      if (completedStepsEqual(completedSteps, current.completed_steps ?? [])) return;
      applyOptimistic({ ...current, completed_steps: completedSteps });
      void persistSession({ completed_steps: completedSteps });
    },
    [applyOptimistic, persistSession],
  );

  const handleWorkflowStepChange = useCallback(
    (next: ReviewWorkflowStep) => {
      const current = sessionRef.current;
      const completingReview = next === "summary";
      if (!current) {
        setLocalStep((prevStep) => {
          setLocalCompleted((prev) =>
            new Set(
              completingReview
                ? normalizeCompletedSteps([...REVIEW_WORKFLOW_STEP_ORDER])
                : normalizeCompletedSteps([...prev, prevStep]),
            ),
          );
          return next;
        });
        return;
      }
      const completedSteps = completingReview
        ? [...REVIEW_WORKFLOW_STEP_ORDER]
        : normalizeCompletedSteps([
            ...(current.completed_steps ?? []),
            toWorkflowStep(current.current_step),
          ]);
      const currentStep = toWorkflowStep(current.current_step);
      if (next === currentStep && completedStepsEqual(completedSteps, current.completed_steps ?? [])) {
        return;
      }
      applyOptimistic({
        ...current,
        current_step: next,
        completed_steps: completedSteps,
      });
      void persistSession({ current_step: next, completed_steps: completedSteps });
    },
    [applyOptimistic, persistSession],
  );

  const handleCompletenessReviewChange = useCallback(
    (next: CompletenessReviewState) => {
      const current = sessionRef.current;
      if (!current) {
        setLocalCompleteness(next);
        return;
      }
      applyOptimistic({ ...current, completeness_review: next });
      void persistSession({ completeness_review: next });
    },
    [applyOptimistic, persistSession],
  );

  const handleChecklistDisputeChange = useCallback(
    (itemKey: string, note: string) => {
      const current = sessionRef.current;
      if (!current) {
        setLocalDisputes((prev) => {
          const next = { ...prev };
          if (!note.trim()) delete next[itemKey];
          else next[itemKey] = note.trim();
          return next;
        });
        return;
      }
      const next = { ...current.checklist_disputes };
      if (!note.trim()) delete next[itemKey];
      else next[itemKey] = note.trim();
      applyOptimistic({ ...current, checklist_disputes: next });
      void persistSession({ checklist_disputes: next });
    },
    [applyOptimistic, persistSession],
  );

  const handleSessionReplace = useCallback(
    (updated: PsRuleHumanReviewRun) => {
      const merged = mergeReviewRunSessionProgress(
        sessionRef.current,
        normalizePsRuleHumanReviewRun(updated),
      );
      const normalized = normalizeCompletedSteps(
        merged.completed_steps ?? [],
        toWorkflowStep(merged.current_step),
        merged,
      );
      const next = completedStepsEqual(normalized, merged.completed_steps ?? [])
        ? merged
        : { ...merged, completed_steps: normalized };
      setSession(next);
      sessionRef.current = next;
      if (setSharedPsRuleReviewRun) setSharedPsRuleReviewRun(next);
    },
    [setSharedPsRuleReviewRun],
  );

  const renameReviewTitle = useCallback(
    async (title: string) => {
      const current = sessionRef.current;
      if (!current) return false;
      setError(null);
      try {
        const updated = await patchGuidedReviewSession(caseId, current.id, { title });
        const merged = mergeReviewRunSessionProgress(current, normalizePsRuleHumanReviewRun(updated));
        setSession(merged);
        sessionRef.current = merged;
        if (setSharedPsRuleReviewRun) setSharedPsRuleReviewRun(merged);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to save review title");
        return false;
      }
    },
    [caseId, setSharedPsRuleReviewRun],
  );

  const saveReviewCopy = useCallback(
    async (title: string) => {
      const current = sessionRef.current;
      if (!current) return false;
      setError(null);
      try {
        await saveGuidedReviewCopy(caseId, current.id, title);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to save review copy");
        return false;
      }
    },
    [caseId],
  );

  const restartReview = useCallback(async () => {
    if (!psRunId) return false;
    setRestarting(true);
    setError(null);
    try {
      const created = await createPsRuleHumanReviewRun(caseId, psRunId, true);
      setSession(created);
      sessionRef.current = created;
      if (useShared && shared) shared.setPsRuleReviewRun(created);
      setLocalStep("narrative");
      setLocalCompleted(new Set());
      setLocalCompleteness(null);
      setLocalDisputes({});
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to restart review");
      return false;
    } finally {
      setRestarting(false);
    }
  }, [caseId, psRunId, shared, useShared]);

  return {
    loading,
    error,
    session,
    persisted,
    psRunId,
    restarting,
    reviewWorkflowStep,
    completedWorkflowSteps,
    completenessReview,
    checklistDisputes,
    handleWorkflowStepChange,
    handleCompletenessReviewChange,
    handleChecklistDisputeChange,
    handleSessionReplace,
    markStepComplete,
    restartReview,
    renameReviewTitle,
    saveReviewCopy,
    reloadSession,
    acknowledgeClearedReview,
    acknowledgingCleared,
  };
}
